/**
 * Application shell: sticky header, role-aware navigation, the account menu
 * (see UserMenu.tsx — identity, design switch, password change, logout) and
 * `<Outlet/>` for the page.
 *
 * Two rules encoded here:
 *
 * 1. NO DEAD LINKS. A nav entry is rendered only when the user actually holds
 *    the permission its route is guarded with (`NAV_ITEMS` mirrors the route
 *    table in App.tsx one-to-one). An auditor therefore sees Dashboard + Triage
 *    and nothing else, instead of three links that answer "Keine Berechtigung".
 *
 * 2. `must_change_password` BLOCKS THE APP. The server does not reject requests
 *    from such a user, so the block is a UI contract: the dialog is rendered
 *    non-dismissable, the navigation is withheld and the page area shows a
 *    notice instead of the routed page. Only "Abmelden" stays reachable, so
 *    nobody gets locked into a dialog they cannot leave.
 *
 * The password change goes through `useAuth().changePassword`, which re-reads
 * `GET /api/auth/me` afterwards — that refresh is what clears the block, since
 * `must_change_password` flips server-side.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { NavItem } from '../lib/types';
import { ProductIcon, ProductLogo } from './BrandLogo';
import { Modal } from './Modal';
import { NavIcon, type NavIconName } from './NavIcon';
import Spinner from './Spinner';
import { ThemeToggle } from './ThemeToggle';
import { useToast } from './Toast';
import { UserMenu } from './UserMenu';

/**
 * Text of the footer line under every page. Empty = no footer at all; set it
 * (e.g. 'AssetRadar · Meldewesen nach CRA') to bring the footer back.
 */
const FOOTER_TEXT = '';

/**
 * Navigation, in display order. Every entry names the permission its route is
 * guarded with in App.tsx — keep the two in sync.
 *
 * The dashboard route itself is intentionally NOT permission-guarded (it is the
 * landing page after login), but the link is labelled with `dashboard.read`
 * anyway: a future role without that permission should not be pointed at a page
 * whose API calls would 403.
 */
type NavSection = 'overview' | 'monitoring' | 'data' | 'admin';

interface ShellNavItem extends NavItem {
  section: NavSection;
  icon: NavIconName;
  /**
   * Rail rendering for a label too long for one line: `railLabel` is the main
   * line, `railTag` a small second line, and `compactLabel` replaces both in the
   * icon-only rail (761–1280 px). `label` stays the full name for the header
   * context, the tooltip and screen readers.
   */
  railLabel?: string;
  railTag?: string;
  compactLabel?: string;
}

const NAV_SECTIONS: readonly { key: NavSection; label: string }[] = [
  { key: 'overview', label: 'Überblick' },
  { key: 'monitoring', label: 'Überwachen' },
  { key: 'data', label: 'Datenraum' },
  { key: 'admin', label: 'Verwaltung' },
];

const NAV_ITEMS: readonly ShellNavItem[] = [
  {
    to: '/',
    label: 'Dashboard',
    permission: 'dashboard.read',
    section: 'overview',
    icon: 'dashboard',
  },
  {
    to: '/cra-check',
    label: 'CRA-Check',
    permission: 'dashboard.read',
    section: 'overview',
    icon: 'check',
  },
  {
    to: '/schwachstellen',
    label: 'Schwachstellen',
    permission: 'dt.read',
    section: 'monitoring',
    icon: 'vulnerability',
  },
  {
    to: '/triage',
    label: 'Triage',
    permission: 'triage.read',
    section: 'monitoring',
    icon: 'triage',
  },
  {
    to: '/audit-log',
    label: 'Audit-Log',
    permission: 'triage.read',
    section: 'monitoring',
    icon: 'audit',
  },
  {
    to: '/verantwortlichkeit',
    label: 'Verantwortlichkeit (PSIRT)',
    railLabel: 'Verantwortlichkeit',
    railTag: 'PSIRT',
    compactLabel: 'PSIRT',
    permission: 'cmdb.read',
    section: 'monitoring',
    icon: 'psirt',
  },
  {
    to: '/stammdaten',
    label: 'Stammdaten',
    permission: 'cmdb.read',
    section: 'data',
    icon: 'database',
  },
  {
    to: '/beziehungen',
    label: 'Beziehungen',
    permission: 'cmdb.read',
    section: 'data',
    icon: 'relations',
  },
  {
    to: '/import',
    label: 'Import',
    permission: 'import.write',
    section: 'data',
    icon: 'import',
  },
  {
    to: '/admin/users',
    label: 'Benutzer',
    permission: 'user.admin',
    section: 'admin',
    icon: 'users',
  },
  {
    to: '/admin/system',
    label: 'System',
    permission: 'system.admin',
    section: 'admin',
    icon: 'system',
  },
];

/**
 * The simple variant is a presentation choice, never an authorisation rule.
 * Deep links and all API permissions stay untouched; the rail concentrates on
 * the daily CRA flow plus System so an administrator can still configure it.
 */
const SIMPLE_NAV_PATHS: ReadonlySet<string> = new Set([
  '/',
  '/cra-check',
  '/schwachstellen',
  '/triage',
  '/audit-log',
  '/verantwortlichkeit',
  '/admin/system',
]);

/**
 * Mirrors `MIN_PASSWORD_LENGTH` in app/auth/security.py. Used for the hint and a
 * cheap pre-check only — the server stays the authority and its German 422
 * detail is displayed verbatim.
 */
const MIN_PASSWORD_LENGTH = 12;

export function Layout(): ReactNode {
  const { user, has, logout } = useAuth();
  const toast = useToast();
  const location = useLocation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const sidebarRef = useRef<HTMLElement>(null);
  const navToggleRef = useRef<HTMLButtonElement>(null);

  // A forced change is not a dialog the user opened, so it cannot be closed.
  const forced = user?.must_change_password === true;
  const showDialog = forced || dialogOpen;

  const navSections = useMemo(
    () =>
      NAV_SECTIONS.map((section) => ({
        ...section,
        items: NAV_ITEMS.filter(
          (item) =>
            item.section === section.key &&
            (user?.app_variant !== 'simple' || SIMPLE_NAV_PATHS.has(item.to)) &&
            (item.permission === null || has(item.permission)),
        ),
      })).filter((section) => section.items.length > 0),
    [has, user?.app_variant],
  );

  const currentItem = useMemo(
    () =>
      NAV_ITEMS.find((item) =>
        item.to === '/'
          ? location.pathname === '/'
          : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`),
      ),
    [location.pathname],
  );

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname, forced]);

  useEffect(() => {
    const compact = window.matchMedia('(max-width: 760px)');
    const onBreakpointChange = (): void => setNavOpen(false);
    compact.addEventListener('change', onBreakpointChange);
    return () => compact.removeEventListener('change', onBreakpointChange);
  }, []);

  useEffect(() => {
    if (!navOpen) return;
    const onPointerDown = (event: PointerEvent): void => {
      if (!sidebarRef.current?.contains(event.target as Node)) setNavOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      setNavOpen(false);
      navToggleRef.current?.focus();
    };
    const onFocusIn = (event: FocusEvent): void => {
      if (!sidebarRef.current?.contains(event.target as Node)) setNavOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, [navOpen]);

  const onLogout = useCallback(async (): Promise<void> => {
    setLoggingOut(true);
    try {
      // `logout()` clears the client state even when the request fails, so
      // RequireAuth takes over and renders the login screen either way.
      await logout();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setLoggingOut(false);
    }
  }, [logout, toast]);

  // RequireAuth guarantees a user; this keeps the types honest without a cast.
  if (!user) return null;

  return (
    <div className="app">
      <a className="skip-link" href="#main-content" onClick={() => setNavOpen(false)}>
        Zum Inhalt springen
      </a>
      <aside ref={sidebarRef} className={`app-sidebar${navOpen ? ' nav-open' : ''}`}>
        {/* The rail is always dark, hence the fixed "negativ" tone. The full logo
            needs 120px; the compact rail (761–1280px) shows the icon instead. */}
        <NavLink to="/" className="sidebar-brand" aria-label="AssetRadar Dashboard">
          <ProductLogo tone="dark" className="sidebar-logo" />
          <ProductIcon tone="dark" className="sidebar-icon" />
        </NavLink>

        {/* Withheld during a forced password change — see rule 2 above. */}
        {forced ? null : (
          <>
            <button
              ref={navToggleRef}
              type="button"
              className="mobile-nav-toggle"
              aria-expanded={navOpen}
              aria-controls="main-navigation"
              aria-label={navOpen ? 'Menü schließen' : 'Menü öffnen'}
              onClick={() => setNavOpen((open) => !open)}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                aria-hidden="true"
              >
                {navOpen ? <path d="m6 6 12 12M18 6 6 18" /> : <path d="M4 6h16M4 12h16M4 18h16" />}
              </svg>
              <span>Menü</span>
            </button>
            <nav id="main-navigation" className="app-nav" aria-label="Hauptnavigation">
              {navSections.map((section) => (
                <div className="nav-group" key={section.key}>
                  <span className="nav-group-label">{section.label}</span>
                  <div className="nav-group-items">
                    {section.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.to === '/'}
                        className={({ isActive }) => (isActive ? 'active' : undefined)}
                        aria-label={item.railLabel ? item.label : undefined}
                        onClick={() => setNavOpen(false)}
                      >
                        <NavIcon name={item.icon} />
                        {item.railLabel ? (
                          <span className="nav-label-stack" aria-hidden="true">
                            <span className="nav-label-full">
                              {item.railLabel}
                              {item.railTag ? <small>{item.railTag}</small> : null}
                            </span>
                            <span className="nav-label-compact">
                              {item.compactLabel ?? item.railLabel}
                            </span>
                          </span>
                        ) : (
                          <span>{item.label}</span>
                        )}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ))}
            </nav>
          </>
        )}

        <a
          className="sidebar-by"
          href="https://brightflare.io/"
          target="_blank"
          rel="noreferrer noopener"
          title="brightflare.io"
        >
          ein Produkt von <strong>BrightFlare</strong>
        </a>
      </aside>

      <div className="app-workspace">
        <header className="app-header">
          <div className="app-context">
            <span className="context-kicker">
              {forced
                ? 'Konto'
                : (NAV_SECTIONS.find((section) => section.key === currentItem?.section)?.label ??
                  'Workspace')}
            </span>
            <svg
              className="context-separator"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              aria-hidden="true"
            >
              <path d="m9 5 7 7-7 7" />
            </svg>
            <strong>{forced ? 'Kontosicherheit' : (currentItem?.label ?? 'AssetRadar')}</strong>
          </div>

          {user.app_variant === 'demo' ? (
            <span className="app-variant-badge demo" title="Messebetrieb mit rücksetzbaren Demo-Daten">
              IT-SA Demo
            </span>
          ) : user.app_variant === 'simple' ? (
            <span
              className="app-variant-badge simple"
              title="Reduzierte Navigation; Berechtigungen und APIs bleiben unverändert"
            >
              Einfach
            </span>
          ) : null}

          <div className="app-user">
            {/*
              During a forced change the menu is withheld along with the nav — its
              "Passwort ändern" entry would open the dialog that is already open,
              and its account block would suggest the app is usable. Only the
              design switch and a plain "Abmelden" stay, so nobody is stranded.
            */}
            {forced ? (
              <>
                <ThemeToggle />
                <button
                  type="button"
                  className="ghost small"
                  onClick={onLogout}
                  disabled={loggingOut}
                >
                  {loggingOut ? <Spinner label="Abmeldung läuft…" /> : 'Abmelden'}
                </button>
              </>
            ) : (
              <UserMenu
                onChangePassword={() => setDialogOpen(true)}
                onLogout={onLogout}
                loggingOut={loggingOut}
              />
            )}
          </div>
        </header>

        <main className="app-main" id="main-content" tabIndex={-1}>
          {forced ? (
            <div className="notice-panel">
              <h1>Passwortwechsel erforderlich</h1>
              <p>
                Bitte ändern Sie Ihr Passwort, bevor Sie AssetRadar weiter nutzen.
              </p>
              <button type="button" className="primary" onClick={() => setDialogOpen(true)}>
                Passwort jetzt ändern
              </button>
            </div>
          ) : (
            <Outlet />
          )}
        </main>

        {FOOTER_TEXT ? <footer className="app-footer">{FOOTER_TEXT}</footer> : null}
      </div>

      {showDialog ? (
        <PasswordDialog forced={forced} onClose={() => setDialogOpen(false)} />
      ) : null}
    </div>
  );
}

interface PasswordDialogProps {
  /** True for the `must_change_password` block: no cancel, no dismissal. */
  forced: boolean;
  onClose: () => void;
}

/**
 * `POST /api/auth/password` — current + new password, plus a local confirmation
 * field so a typo does not become a lockout. The server's German detail (401 for
 * a wrong current password, 422 for a weak new one) is shown verbatim.
 */
function PasswordDialog({ forced, onClose }: PasswordDialogProps): ReactNode {
  const { changePassword } = useAuth();
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const mismatch = confirm !== '' && next !== confirm;
  const tooShort = next !== '' && next.length < MIN_PASSWORD_LENGTH;
  const canSubmit =
    !saving && current !== '' && next !== '' && confirm !== '' && !mismatch && !tooShort;

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      await changePassword({ current_password: current, new_password: next });
      toast.success('Passwort geändert.');
      // Not reached when `forced` was true: the refresh inside changePassword
      // clears must_change_password, and Layout unmounts this dialog.
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title="Passwort ändern"
      subtitle={
        forced
          ? 'Für dieses Konto ist eine Passwortänderung erforderlich.'
          : 'Alle anderen Sitzungen dieses Kontos werden dabei abgemeldet.'
      }
      onClose={onClose}
      size="narrow"
      // A forced change must not be escapable; a voluntary one must not be
      // dismissed while the request is in flight.
      dismissable={!forced && !saving}
      footer={
        <>
          <button type="submit" form="pw-form" className="primary" disabled={!canSubmit}>
            {saving ? <Spinner variant="on-accent" label="Wird gespeichert…" /> : 'Speichern'}
          </button>
          {forced ? null : (
            <button type="button" className="ghost" onClick={onClose} disabled={saving}>
              Abbrechen
            </button>
          )}
        </>
      }
    >
      <form id="pw-form" className="stack" onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor="pw-current">Aktuelles Passwort</label>
          <input
            id="pw-current"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
            disabled={saving}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="pw-new">Neues Passwort</label>
          <input
            id="pw-new"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(event) => setNext(event.target.value)}
            aria-invalid={tooShort}
            aria-describedby="pw-new-hint"
            disabled={saving}
            required
          />
          <span className="hint" id="pw-new-hint">
            Mindestens {MIN_PASSWORD_LENGTH} Zeichen. Passphrasen sind erlaubt und erwünscht.
          </span>
          {tooShort ? (
            <span className="err">
              Passwort muss mindestens {MIN_PASSWORD_LENGTH} Zeichen lang sein.
            </span>
          ) : null}
        </div>
        <div className="field">
          <label htmlFor="pw-confirm">Neues Passwort bestätigen</label>
          <input
            id="pw-confirm"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            aria-invalid={mismatch}
            disabled={saving}
            required
          />
          {mismatch ? <span className="err">Die Passwörter stimmen nicht überein.</span> : null}
        </div>
        {error !== null ? (
          <div className="banner error" role="alert">
            {error}
          </div>
        ) : null}
      </form>
    </Modal>
  );
}

export default Layout;
